import {
  withCompInfo,
  createComponentMapperModel,
} from '@wix/editor-elements-integrations';
import type { PhysicalAlignment } from '@wix/editor-elements-common-utils';
import {
  getScaledFont,
  addUnitToEveryField,
  withFallbackValues,
  castFromInheritIfNeeded,
  convertPhysicalInputAlignmentToLogical,
  convertPhysicalInputAlignmentToDirection,
  getHeightInPixels,
  withSiteDirection,
} from '@wix/editor-elements-common-utils';
import {
  getInputHeight,
  getRequiredIndicationDisplay,
  getLabelPaddingsValues,
  getTextPaddingsValues,
} from '@wix/editor-elements-common-utils/src/commons/inputUtils';
import type {
  TextInputDefinition,
  ITextInputMapperProps,
  TextInputCSSVars,
  TextInputType,
  TextInputStyleProperties,
} from '../TextInput.types';
import { textTypeToName, migratedSPFields, TranslationKeys } from './constants';

const getName = (
  textType: TextInputType,
  alt: string | undefined,
): string | undefined => {
  if (textTypeToName[textType]) {
    return textTypeToName[textType];
  } else if (alt) {
    return alt.toLowerCase().replace(' ', '-');
  }
  return undefined;
};

export const props = withCompInfo<ITextInputMapperProps, TextInputDefinition>()(
  ['compData', 'compProps', 'skin', 'translate', 'experiments'],
  ({ compData, compProps, skin, translate, experiments }) => {
    const translations = {
      phoneFormatLengthValidationError: translate(
        TranslationKeys.NAMESPACE,
        TranslationKeys.PHONE_FORMAT_LENGTH_VALIDATION_ERROR,
      ),
      phoneFormatDefaultValidationError: translate(
        TranslationKeys.NAMESPACE,
        TranslationKeys.PHONE_FORMAT_DEFAULT_VALIDATION_ERROR,
      ),
      phoneFormatComplexPhoneDefaultValidationError: translate(
        TranslationKeys.NAMESPACE,
        TranslationKeys.PHONE_FORMAT_COMPLEX_PHONE_DEFAULT_VALIDATION_ERROR,
      ),
      errorMessage: translate(
        TranslationKeys.NAMESPACE,
        TranslationKeys.INLINE_ERROR_MESSAGE_DEFAULT_ERROR_MESSAGE,
      ),
    };
    const { 'specs.thunderbolt.TextInputAutoFillFix': textInputAutoFillFix } =
      experiments;

    return {
      skin:
        skin === 'ResponsiveTextInputSkin'
          ? 'ResponsiveTextInputSkin'
          : 'AppsTextInputSkin',
      value: compData.value || '',
      inputType: compData.textType,
      label: compData.label,
      name: getName(
        compData.textType,
        compData.label || compData.placeholder || compProps.placeholder,
      ),
      placeholder: compData.placeholder || compProps.placeholder,
      readOnly: compProps.readOnly || false,
      required: compProps.required,
      autoComplete:
        typeof compData.autoComplete === 'string'
          ? compData.autoComplete === 'on'
          : compData.textType !== 'password' && compProps.autoComplete,
      autoComplete_:
        compData.textType !== 'password' && compProps.autoComplete
          ? 'on'
          : null,
      isDisabled: compProps.isDisabled || false,
      pattern: compData.pattern,
      maxLength: compData.maxLength,
      ...(compData.textType === 'number' && {
        min: compData.min,
        max: compData.max,
        step: compData.step,
      }),
      prefix: compData.prefix,
      phoneFormat: compData.phoneFormat,
      errorMessageType: compProps.errorMessageType,
      translations,
      textInputAutoFillFixEnabled: String(textInputAutoFillFix) === 'true',
    };
  },
);

export const css = withCompInfo<TextInputCSSVars, TextInputDefinition>()(
  [
    'compLayout',
    'compSingleLayout',
    'compProps',
    'styleProperties',
    'isMobileView',
    'hasResponsiveLayout',
    'compData',
    'siteFonts',
    'formatCssValue',
    'experiments',
    'siteFontsSpxResolved',
    'isOneDocMigrated',
  ],
  ({
    compLayout,
    compSingleLayout,
    compProps,
    styleProperties,
    isMobileView,
    hasResponsiveLayout,
    compData,
    siteFonts,
    formatCssValue: formatCssValueWithSpx,
    siteFontsSpxResolved,
    isOneDocMigrated,
  }) => {
    const { inputHeightMobile } = compProps;
    const {
      prefix,
      direction = 'inherit',
      labelDirection = 'inherit',
      inputDirection = 'inherit',
      errorDirection = 'inherit',
    } = compData;

    const {
      textPadding,
      labelMargin,
      labelPadding,
      inputHeight,
      spx,
      align,
      labelAlign,
      inputAlign,
    } = styleProperties as TextInputStyleProperties;
    const spxProps = { formatCssValueWithSpx, spx };

    const compInfo = {
      styleProperties,
      compLayout,
      compProps,
      isMobileView,
      siteFonts,
      siteFontsSpxResolved,
    };

    const calculatedLabelAlign = castFromInheritIfNeeded(labelAlign, align);
    const calculatedInputAlign = castFromInheritIfNeeded(inputAlign, align);

    const textPaddingValues = getTextPaddingsValues({
      textPadding,
      hasPrefix: !!prefix,
      align: calculatedInputAlign,
    });
    const labelPaddingValues = getLabelPaddingsValues({
      labelPadding,
      align: calculatedLabelAlign,
    });

    const sharedProps = {
      '--fnt': getScaledFont(
        compInfo,
        'fnt',
        compProps.inputMobileFontSize,
        13,
      ),
      '--fntlbl': getScaledFont(
        compInfo,
        'fntlbl',
        compProps.labelMobileFontSize,
        16,
      ),
      '--direction': withSiteDirection(direction),
      '--align': align,
      '--labelDirection': labelDirection,
      '--inputDirection': inputDirection,
      '--errorDirection': errorDirection,
      '--requiredIndicationDisplay':
        getRequiredIndicationDisplay(styleProperties),
      '--labelMarginBottom': formatCssValueWithSpx(labelMargin, spx),
      ...addUnitToEveryField(textPaddingValues, textPadding, spxProps),
      ...addUnitToEveryField(labelPaddingValues, labelPadding, spxProps),
    };

    if (hasResponsiveLayout) {
      return sharedProps;
    } else {
      const height = isOneDocMigrated
        ? getHeightInPixels(compSingleLayout)
        : compLayout.height;

      return {
        ...sharedProps,
        ...(!isOneDocMigrated && {
          height: 'auto',
        }),
        '--inputHeight': getInputHeight(
          {
            inputHeightProps: { inputHeightMobile, inputHeight },
            height,
            isMobileView,
          },
          formatCssValueWithSpx,
          spx,
        ),
      };
    }
  },
  [
    withFallbackValues([
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'styleProperties',
        fields: [...migratedSPFields, 'textAlignment'],
      },
      {
        sourceNamespace: 'styleProperties',
        targetNamespace: 'styleProperties',
        fields: [{ source: 'textAlignment', target: 'align' }],
        enhancer: convertPhysicalInputAlignmentToLogical,
      },
      {
        sourceNamespace: 'styleProperties',
        targetNamespace: 'compData',
        fields: [{ source: 'textAlignment', target: 'direction' }],
        enhancer: (textAlignment, { compData }) => {
          if (compData.phoneFormat) {
            return 'inherit';
          }
          return convertPhysicalInputAlignmentToDirection(
            textAlignment as PhysicalAlignment,
          );
        },
      },
    ]),
  ],
);

export default createComponentMapperModel({ props, css });
